/**
 * Premium "Logo Ring Burst" intro.
 *
 * Visual design:
 *  - Logo at center; four concentric rings burst outward sequentially with
 *    fade. Particle dots radiate at the same time. After bursts settle,
 *    headline reveals with an underline.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { rng } from "../../util/rng.js";

export const logoRingBurst: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;
  const logoH = isVertical ? 200 : 170;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const stageId = `${clipPrefix}-stage`;
  const logoId = `${clipPrefix}-logo`;
  const lineId = `${clipPrefix}-line`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const ringCount = 4;
  const ringSize = isVertical ? 360 : 320;
  const ringIds = Array.from({ length: ringCount }).map((_, i) => `${clipPrefix}-r${i}`);
  const rings = ringIds
    .map(
      (id) =>
        `<div id="${id}" style="position:absolute;left:50%;top:50%;width:${ringSize}px;height:${ringSize}px;margin-left:-${ringSize / 2}px;margin-top:-${ringSize / 2}px;border-radius:9999px;border:3px solid ${accent};box-shadow:0 0 30px ${withAlpha(accent, 0.55)};opacity:0;"></div>`,
    )
    .join("");

  // Particle dots radiating outward.
  const particleCount = 22;
  const r = rng(91);
  const particleIds: string[] = [];
  const particleHtml = Array.from({ length: particleCount })
    .map((_, i) => {
      const angle = (i / particleCount) * 360 + r() * 12;
      const id = `${clipPrefix}-p${i}`;
      particleIds.push(id);
      const size = 6 + r() * 6;
      const color = i % 3 === 0 ? accent : themeColors.fg;
      return `<div id="${id}" data-angle="${angle}" style="position:absolute;left:50%;top:50%;width:${size}px;height:${size}px;margin-left:-${size / 2}px;margin-top:-${size / 2}px;border-radius:9999px;background:${color};box-shadow:0 0 10px ${color};opacity:0;"></div>`;
    })
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;">
    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— Welcome —</div>
    <div id="${stageId}" style="position:relative;width:${ringSize * 1.6}px;height:${ringSize * 1.6}px;display:flex;align-items:center;justify-content:center;">
      ${rings}
      ${particleHtml}
      <div id="${logoId}" style="position:relative;filter:drop-shadow(0 12px 30px ${withAlpha(accent, 0.6)});">
        ${brandLogoImg({ id: `${logoId}-img`, sizePx: logoH })}
      </div>
    </div>
    <div id="${lineId}" style="height:2px;width:0;background:${accent};box-shadow:0 0 16px ${withAlpha(accent, 0.7)};"></div>
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};opacity:0;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  // Particle radial via inline transforms — precompute.
  const radial = (angleDeg: number, dist: number) => {
    const a = (angleDeg * Math.PI) / 180;
    return { x: Math.cos(a) * dist, y: Math.sin(a) * dist };
  };

  const init: string[] = [
    `gsap.set("#${logoId}", { opacity: 0, scale: 0.5 });`,
    `gsap.set("#${lineId}", { width: 0 });`,
    `gsap.set("#${eyeId}", { opacity: 0, y: -16 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 28 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
  ];
  ringIds.forEach((id) => init.push(`gsap.set("#${id}", { opacity: 0, scale: 0.4 });`));
  particleIds.forEach((id, i) => {
    const angle = (i / particleCount) * 360 + r() * 12;
    void angle;
    init.push(`gsap.set("#${id}", { opacity: 0, scale: 0.4 });`);
  });

  const tweens: string[] = [
    // Logo pops in first.
    t.to(`#${logoId}`, { opacity: 1, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 0.1),
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.2),
  ];

  // Ring sequential bursts.
  ringIds.forEach((id, i) => {
    const at = 0.2 + i * 0.16;
    tweens.push(t.fromTo(`#${id}`, { opacity: 0.95, scale: 0.4 }, { opacity: 0, scale: 3.2, duration: 1.05, ease: "raw:\"power3.out\"" }, at));
  });

  // Particles burst outward.
  particleIds.forEach((id, i) => {
    const angle = (i / particleCount) * 360;
    const { x, y } = radial(angle, isVertical ? 380 : 340);
    const at = 0.25 + (i % 5) * 0.06;
    tweens.push(t.fromTo(`#${id}`, { opacity: 1, scale: 1, x: 0, y: 0 }, { opacity: 0, scale: 0.4, x, y, duration: 0.95, ease: "raw:\"power3.out\"" }, at));
  });

  // Underline + headline + sub.
  tweens.push(t.to(`#${lineId}`, { width: 90, duration: 0.45, ease: "raw:\"power2.out\"" }, 1.0));
  tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.05));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.25));

  // Subtle logo breathe.
  const dwell = Math.max(0.6, clipDurationSec - 1.6);
  tweens.push(t.to(`#${logoId}`, { scale: 1.06, duration: dwell, ease: "raw:\"sine.inOut\"" }, 1.4));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init: init.join("\n"), tweens }]);
};
